import { axiosJsonInstance } from 'src/store/api';
import { getSimpleMinutesAndSeconds } from 'src/utils/helpers';
import { ApiResponse, Form, Polygon, PointProps } from '../types';

const TOKEN = localStorage.getItem('token');

const normalizePolygonPoints = (points: PointProps[], canvasWidth: number, canvasHeight: number) => {
  return points.map((point) => ({
    x: parseFloat((point.x / canvasWidth).toFixed(4)),
    y: parseFloat((point.y / canvasHeight).toFixed(4)),
  }));
};

const generateId = (prefix = '') => {
  const randomPart = Math.random().toString(36).substring(2, 10);
  const timestampPart = Date.now().toString(36);

  return prefix ? `${prefix}_${randomPart}${timestampPart}` : `${randomPart}${timestampPart}`;
};

const denormalizePolygonPoints = (points: { x: number, y: number }[], canvasWidth: number, canvasHeight: number) => {
  return points.map((point) => ({
    id: generateId('point'),
    x: Math.round(point.x * canvasWidth),
    y: Math.round(point.y * canvasHeight),
  }));
};

export const savePolygon = async (
  polygon: Polygon,
  polygonName: string,
  canvasWidth: number,
  canvasHeight: number,
  departmentId: number,
  cameraId: number,
) => {
  try {
    const normalizedCoordinates = normalizePolygonPoints(polygon.points, canvasWidth, canvasHeight);


    const payload = {
      name: polygonName || `Polygon ${new Date().toISOString()}`,
      coordinates: normalizedCoordinates,
      department: departmentId,
      camera: cameraId,
      created_by: 'web application',
    };

    if (polygon.backendId) {
      const response = await axiosJsonInstance.put(`polygons/${polygon.backendId}/`, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${TOKEN}`,
        },
      });
      return response.data;
    }
    const response = await axiosJsonInstance.post('polygons/', payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${TOKEN}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error saving polygon:', error);
    throw error;
  }
};

export const getPolygons = async () => {
  try {
    return await axiosJsonInstance.get('polygons/', {
      headers: {
        Authorization: `Token ${localStorage.getItem('token')}`,
      },
    });
  } catch (error) {
    console.error('Error fetching polygons:', error);
  }
  return [];
};

export const fetchPolygons = async (canvasWidth: number, canvasHeight: number, cameraId: number) => {
  try {
    const response = await axiosJsonInstance.get('polygons/', {
      headers: {
        Authorization: `Token ${TOKEN}`,
      },
      params: {
        camera_id: cameraId,
      },
    });
    const filteredPolygons = response.data.filter((item: any) => item.camera === cameraId);    
    const polygons: Polygon[] = filteredPolygons.map((item: any, index: number) => ({
      id: generateId('polygon'),
      backendId: item.id,
      points: denormalizePolygonPoints(item.coordinates, canvasWidth, canvasHeight),
      isSelected: false,
      name: item.name,
    }));

    return polygons;
  } catch (error) {
    console.error('Error fetching polygons:', error);
    throw error;
  }
};

export const fetchPolygonMetrics = async (
  formValues: Form,
  startTimeTimestamp: number,
  endTimeTimestamp: number,
  departmentId: number,
  cameraId: number,
  storeId: number,
) => {
  try {
    const response = await axiosJsonInstance.get('polygon-metrics/', {
      params: {
        camera_id: cameraId,
        start_date: startTimeTimestamp,
        end_date: endTimeTimestamp,
        x1: formValues.x1,
        x2: formValues.x2,
        y1: formValues.y1,
        y2: formValues.y2,
        x3: formValues.x3,
        y3: formValues.y3,
        x4: formValues.x4,
        y4: formValues.y4,
      },
    });

    const data = response.data as ApiResponse;

    const parsedResults = [
      { metric: 'Total Detections', value: data.summary.total_detections },
      { metric: 'Unique Track IDs', value: data.summary.unique_track_ids },
      { metric: 'X1', value: formValues.x1 },
      { metric: 'Y1', value: formValues.y1 },
      { metric: 'X2', value: formValues.x2 },
      { metric: 'Y2', value: formValues.y2 },
      { metric: 'X3', value: formValues.x3 },
      { metric: 'Y3', value: formValues.y3 },
      { metric: 'X4', value: formValues.x4 },
      { metric: 'Y4', value: formValues.y4 },
    ];

    const parsedBusiestDays = data.busiest_days ? data.busiest_days.map((day) => ({
      date: day.date,
      unique_visitors: day.unique_visitors,
      avg_time_spent_seconds: day.avg_time_spent_seconds
        ? String(getSimpleMinutesAndSeconds(Number(day.avg_time_spent_seconds)))
        : '-',
      total_time_spent_seconds: day.total_time_spent_seconds
        ? String(getSimpleMinutesAndSeconds(Number(day.total_time_spent_seconds)))
        : '-',
    })) : [];

    return {
      results: parsedResults,
      busiestDays: parsedBusiestDays,
    };
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
};

export const deletePolygon = async (polygonId: number) => {
  try {
    if (!polygonId) {
      throw new Error('Cannot delete polygon: Missing backend ID');
    }

    const response = await axiosJsonInstance.delete(`polygons/${polygonId}/`, {
      headers: {
        Authorization: `Token ${TOKEN}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error deleting polygon:', error);
    throw error;
  }
};
